// React component dependencies and Firebase/Auth config
import React from "react";
import { auth, provider } from "../../config/firebase-config"; // Firebase Auth config
import { signInWithPopup } from "firebase/auth"; // Google popup login
import { useNavigate } from "react-router-dom"; // Navigation after login

// UI components from MUI
import {
  Button,
  Box,
  Typography,
  Container,
  Paper,
  useTheme,
} from "@mui/material";

// Google icon and animation utility
import { FcGoogle } from "react-icons/fc"; // 🟦 Google logo icon
import { motion } from "framer-motion"; // 🎞️ Animation wrapper

// Login page component
const LoginPage = () => {
  const navigate = useNavigate();        // 🔁 To navigate after login
  const theme = useTheme();              // 🎨 Access Material-UI theme for styling

  // Handles Google authentication and redirects on success
  const handleLogin = async () => {
    try {
      await signInWithPopup(auth, provider); // Sign in with Google popup
      navigate("/");                         // Redirect to homepage on success
    } catch (err) {
      console.error("Login failed:", err);   // Log errors if authentication fails
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: theme.palette.background.default, // Match theme background
        px: 2,
      }}
    >
      <Container maxWidth="sm">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Paper
            elevation={4}
            sx={{
              p: 5,
              borderRadius: theme.shape.borderRadius * 2,
              textAlign: "center",
              boxShadow: `0 8px 30px ${theme.palette.divider}`, // subtle drop shadow
            }}
          >

            <Typography
              variant="h3"
              gutterBottom
              sx={{ fontWeight: "bold", textAlign: "center" }}
            >
              Resqgen
            </Typography>

            {/* Right-to-left layout for Hebrew text */}
            <Box dir="rtl" sx={{ textAlign: "center" }}>
              {/* 📝 Short Hebrew intro */}
              <Typography
                variant="subtitle1"
                color="text.secondary"
                gutterBottom
              >
                צרו שאלות מורכבות במהירות ובקלות!
              </Typography>

              {/* Google sign-in button */}
              <Button
                variant="outlined"
                onClick={handleLogin}
                startIcon={<FcGoogle size={24} />}
                sx={{
                  mt: 4,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "1rem",
                  px: 3,
                  minWidth: "280px",
                  borderColor: theme.palette.divider,
                  color: theme.palette.text.primary,
                  backgroundColor: theme.palette.background.paper,
                  "&:hover": {
                    backgroundColor: theme.palette.action.hover,
                    boxShadow: `0 0 8px ${theme.palette.divider}`,
                  },
                  mx: "auto",
                  gap: 1,
                }}
                fullWidth
                size="large"
              >
                התחברות עם Google
              </Button>

              {/* Privacy disclaimer */}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 2, display: "block" }}
              >
                ההתחברות מאובטחת דרך Google. אנחנו לא שומרים את פרטי הכניסה שלך.
              </Typography>
            </Box>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
};

export default LoginPage;
